import { useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { motion, useScroll, useTransform } from 'framer-motion'
import { useTranslation } from 'react-i18next'

export default function Footer() {
  const { t, i18n } = useTranslation()
  const es = i18n.language?.startsWith('es')
  const ref = useRef(null)
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end end'],
  })

  const y = useTransform(scrollYProgress, [0, 1], [60, 0])
  const opacity = useTransform(scrollYProgress, [0, 0.5], [0, 1])

  const [email, setEmail] = useState('')
  const [status, setStatus] = useState(null) // null | 'sending' | 'done' | 'error'

  async function handleNotify(e) {
    e.preventDefault()
    const trimmed = email.trim()
    if (!trimmed) return

    setStatus('sending')
    try {
      const res = await fetch('/api/notify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmed }),
      })
      if (!res.ok) throw new Error()
      setStatus('done')
      setEmail('')
    } catch {
      setStatus('error')
    }
  }

  return (
    <motion.footer className="site-footer" ref={ref} style={{ y, opacity }}>
      {/* Notify form */}
      <div className="footer-notify">
        <p className="footer-notify-label">{t('footer.notify')}</p>
        {status === 'done' ? (
          <p className="footer-notify-done">{t('footer.notifyDone')}</p>
        ) : (
          <form className="footer-notify-form" onSubmit={handleNotify}>
            <input
              type="email"
              placeholder={t('footer.notifyPlaceholder')}
              value={email}
              onChange={(e) => { setEmail(e.target.value); if (status === 'error') setStatus(null); }}
              onFocus={(e) => {
                const el = e.target
                setTimeout(() => {
                  el.scrollIntoView({ behavior: 'smooth', block: 'center' })
                }, 600)
              }}
              required
              className="footer-notify-input"
            />
            <button
              type="submit"
              className="footer-notify-btn"
              disabled={status === 'sending'}
            >
              {status === 'sending' ? t('footer.notifySending') : t('footer.notifyBtn')}
            </button>
          </form>
        )}
        {status !== 'done' && (
          <p style={{ fontSize: '0.65em', opacity: 0.5, marginTop: 4 }}>
            {es ? 'Al suscribirte, aceptas recibir emails. Puedes cancelar en cualquier momento.' : 'By subscribing, you agree to receive emails. Unsubscribe anytime.'}
          </p>
        )}
        {status === 'error' && <p className="footer-notify-error">{t('footer.notifyError')}</p>}
      </div>

      <div className="footer-top">
        <div className="f-logo">Opus Magnum Ghost</div>
        <nav className="footer-links" aria-label="Footer navigation">
          <div className="footer-col">
            <h2 className="footer-col-title">{t('footer.shop')}</h2>
            <ul>
              <li><Link to="/login" className="footer-link-animated">The Diagnostic Set</Link></li>
              <li><Link to="/login" className="footer-link-animated">The Sovereign System</Link></li>
              <li><Link to="/login" className="footer-link-animated">The Apex Collection</Link></li>
            </ul>
          </div>
          <div className="footer-col">
            <h2 className="footer-col-title">{t('footer.company')}</h2>
            <ul>
              <li><Link to="/about" className="footer-link-animated">{t('footer.about')}</Link></li>
              <li><Link to="/ingredients" className="footer-link-animated">{es ? 'Ingredientes' : 'Ingredients'}</Link></li>
              <li><Link to="/layering" className="footer-link-animated">{es ? 'Guía de Capas' : 'Layering Guide'}</Link></li>
              <li><Link to="/ecosystem" className="footer-link-animated">{es ? 'Ecosistema' : 'Ecosystem'}</Link></li>
              <li><Link to="/blog" className="footer-link-animated">Blog</Link></li>
              <li><a href="mailto:ghost@opusmagnumghost.com" className="footer-link-animated">{t('footer.contact')}</a></li>
            </ul>
          </div>
          <div className="footer-col">
            <h2 className="footer-col-title">{es ? 'Fragancias' : 'Fragrances'}</h2>
            <ul>
              <li><Link to="/fragrances/ghost" className="footer-link-animated">Ghost</Link></li>
              <li><Link to="/fragrances/predator" className="footer-link-animated">Predator</Link></li>
              <li><Link to="/fragrances/euphoria" className="footer-link-animated">Euphoria</Link></li>
              <li><Link to="/fragrances/intimacy" className="footer-link-animated">Intimacy</Link></li>
              <li><Link to="/fragrances/animal" className="footer-link-animated">Animal</Link></li>
              <li><Link to="/fragrances/aftermath" className="footer-link-animated">Aftermath</Link></li>
            </ul>
          </div>
          <div className="footer-col">
            <h2 className="footer-col-title">{t('footer.account')}</h2>
            <ul>
              <li><Link to="/dashboard" className="footer-link-animated">{t('nav.dashboard')}</Link></li>
              <li><Link to="/login" className="footer-link-animated">{t('footer.signIn')}</Link></li>
            </ul>
          </div>
          <div className="footer-col">
            <h2 className="footer-col-title">{t('footer.legal')}</h2>
            <ul>
              <li><Link to="/privacy" className="footer-link-animated">{t('footer.privacy')}</Link></li>
              <li><Link to="/terms" className="footer-link-animated">{t('footer.terms')}</Link></li>
              <li><Link to="/returns" className="footer-link-animated">{t('footer.returns')}</Link></li>
              <li><Link to="/shipping" className="footer-link-animated">{t('footer.shipping')}</Link></li>
              <li><Link to="/aviso-legal" className="footer-link-animated">Aviso Legal</Link></li>
              <li><Link to="/withdrawal" className="footer-link-animated">Withdrawal / Desistimiento</Link></li>
              <li><button className="footer-link-animated footer-cookie-btn" onClick={() => { localStorage.removeItem('omg_cookie_consent'); window.dispatchEvent(new Event('omg-cookie-reset')) }}>{t('cookieManage')}</button></li>
            </ul>
          </div>
        </nav>
      </div>
      <div className="footer-bottom">
        <p>&copy; {new Date().getFullYear()} Opus Magnum Ghost. {t('footer.rights')}</p>
        <p className="footer-disclaimer">
          {t('footer.disclaimer')}
        </p>
      </div>
    </motion.footer>
  )
}
